/* global fbq */
/**
 *
 *  Lifestyle Upcoming page
 *  Component
 *
 *  Created by Vladimir Shishlyannikov on 07/03/2024.
 *  Copyright (c) 2023-present iWoo Ltd. All rights reserved.
 *
 */

// src/components/LifestyleLP/LifestyleUpcomingPage/LifestyleUpcomingPage.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import moment from "moment";
import { Title, Meta, Link as LinkMeta } from "react-meta-elements";

import { useAppState } from "../../../state";
import { usePrevState } from "../../../context/PrevStateProvider";
import { useRegion } from "../../../context/RegionProvider";

// Components
import Header from "../../Header/Header";
import Footer from "../../Footer/Footer";
import ScrollToTopOnMount from "../../ScrollToTopOnMount/ScrollToTopOnMount";
import Button from "../../Button/Button";
import EventCard from "../EventCard/EventCard";
import EventCardPlaceholder from "../../EventCardPlaceholder/EventCardPlaceholder";
import BreadcrumbNav from "../../BreadcrumbNav/BreadcrumbNav";
import HeadlineWithSeparator from "../../HeadlineWithSeparator/HeadlineWithSeparator";
import CityPickerButton from "../../CityPickerButton/CityPickerButton";

// Hooks
import useTheme from "../../ThemeProvider/useTheme/useTheme";
import useLatest from "../../../hooks/useLatest/useLatest";
import {
  useGetUpcomingEvents,
  useGetEvents,
} from "../../../hooks/useEvents/useEvents";

// Utils
import { getTz } from "../../../utils/date";
import { PAGE_SIZE } from "../../../constants";
import { groupBy } from "../../../utils/_";

// Icons
import ArrowLeftIcon from "../../../icons/ArrowLeftIcon";
import ArrowRightIcon from "../../../icons/ArrowRightIcon";

// Images

export default function LifestyleUpcomingPage() {
  const { theme } = useTheme();
  const { region, city: globalCity } = useRegion();
  const { user, hasTriedToAutoLogin, isLoggedIn } = useAppState();

  const [selectedCity, setSelectedCity] = useState(globalCity);

  useEffect(() => {
    if (globalCity !== selectedCity) {
      setSelectedCity(globalCity);
    }
  }, [globalCity, selectedCity]);

  // Определяем, готовы ли мы к загрузке
  const isReady = Boolean(hasTriedToAutoLogin && region && selectedCity);

  const {
    events,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isPending: isLoading,
    isError,
    error,
    cursorRef,
    totalEvents,
    hasEvents,
  } = useGetEvents({
    customerId: user?.id,
    status: ["UPCOMING", "STARTED", "UPCOMING_FULL"],
    region: user?.region || region,
    city: user?.city || selectedCity,
    sort: "start",
    size: PAGE_SIZE,
    enabled: isReady, // Включаем запрос только когда готовы
  });

  const heroRef = React.useRef(null);
  const sliderRef = React.useRef(null);

  useEffect(() => {
    if (sliderRef.current && events && events.length) {
      setTimeout(() => {
        sliderRef.current &&
          sliderRef.current.scrollTo({
            left: sliderRef.current.scrollWidth,
            behavior: "smooth",
          });
      }, 1000);
    }
  }, [sliderRef.current, events]);

  // Группируем события по месяцам
  const futureEvents = useMemo(() => {
    return groupBy(events, (evt) =>
      moment.utc(evt.start).tz(getTz(region)).format("MMMM YYYY")
    );
  }, [events, region]);

  // Получаем ключи групп в правильном порядке (хронологическом)
  const eventGroups = useMemo(() => {
    const groups = Object.keys(futureEvents);
    // Сортируем группы по дате
    return groups.sort((a, b) => {
      const dateA = moment(a, "MMMM YYYY");
      const dateB = moment(b, "MMMM YYYY");
      return dateA - dateB;
    });
  }, [futureEvents]);

  // Получаем последнюю группу
  const lastGroupKey = eventGroups[eventGroups.length - 1];
  const lastGroupEvents = lastGroupKey ? futureEvents[lastGroupKey] : [];

  // Вычисляем количество плейсхолдеров для последней группы
  const getPlaceholderCount = useCallback((currentEventsCount) => {
    if (currentEventsCount === 0) return 6;
    const remainder = currentEventsCount % 6;
    return remainder === 0 ? 6 : 6 - remainder;
  }, []);

  const placeholderCount = getPlaceholderCount(lastGroupEvents.length);

  const isUserLoggedIn = !!(user && user.id);

  const handleCityChange = useCallback(
    (newCity) => {
      if (selectedCity === newCity) return;
      setSelectedCity(newCity);
    },
    [selectedCity]
  );

  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  // So that when the user gets back, it'll reload the cards
  const handleEventCardClick = useCallback(() => {
    // It's being cleared on Become a Member page?
    if (!user) {
      // Логика для неавторизованных пользователей
    }
  }, [user]);

  // Определяем состояния отображения
  const showInitialLoader = isLoading && !hasEvents;
  const showContent = hasEvents || isLoading;
  const showNextPageLoader = isFetchingNextPage && hasEvents;

  return (
    <>
      <ScrollToTopOnMount />
      <Title>TruBe: Unique Locations, Unforgettable Fitness Events</Title>
      <Meta
        name="description"
        content="TruBe: Unique Locations, Unforgettable Fitness classes in Pilates, Yoga, HIIT, Cardio, and more. Providing the best coaches and fitness and wellbeing experience."
      />
      <LinkMeta rel="canonical" href="https://trubeapp.com/events/upcoming" />

      <Header
        // behaviour="none"
        showRegionSelect={false}
        isFixed
        isLoggedIn={isUserLoggedIn}
        returnTo="/lifestyle"
        heroRef={heroRef}
      />
      <div />

      <div className="tbk-overflow-hidden">
        <div className="tbk-container tbk-mx-auto tbk-px-2 lg:tbk-px-8">
          <div
            ref={sliderRef}
            className="tb-slider tbk-mb-3 tbk-mt-3 tbk-flex tbk-flex-nowrap tbk-items-center"
          >
            <BreadcrumbNav to={"/lifestyle"}>
              Lifestyle collective
            </BreadcrumbNav>

            <ArrowRightIcon className="tbk-text-blue-grey-light" />

            <BreadcrumbNav>Upcoming Classes</BreadcrumbNav>
          </div>
        </div>
      </div>

      <div className="tbk-flex tbk-flex-col tbk-items-center tbk-justify-center tbk-pb-4 tbk-pt-4 lg:tbk-pt-4">
        <div className="tbk-container tbk-px-2 lg:tbk-px-8">
          <h3 className="tbk-text-title-bold lg:tbk-text-title-bold tbk-bg-basic-white tbk-px-2 tbk-text-center tbk-text-center tbk-text-blue-grey">
            Upcoming Classes
          </h3>

          {region === "US" && (
            <CityPickerButton
              className="tbk-mt-4"
              city={selectedCity}
              onCityChange={handleCityChange}
            />
          )}

          {isError && (
            <div className="tbk-text-red-500 tbk-mt-4 tbk-text-center">
              Error loading events: {error?.message}
            </div>
          )}

          {/* Первоначальная загрузка */}
          {showInitialLoader && (
            <div className="tbk-pt-4">
              <HeadlineWithSeparator
                component="h2"
                titleClass="tbk-bg-basic-white tbk-text-blue-grey-light tbk-text-h3-subtitle lg:tbk-text-title-bold"
                className="tbk-mb-4 lg:tbk-mb-6"
                separatorClass={"tbk--mt-2 lg:tbk--mt-2"}
              >
                Loading Events
              </HeadlineWithSeparator>
              <div className="tbk-mb-4 tbk-grid tbk-gap-2 md:tbk-grid-cols-2 lg:tbk-grid-cols-3">
                {[...Array(6)].map((_, index) => (
                  <EventCardPlaceholder key={`initial-${index}`} />
                ))}
              </div>
            </div>
          )}

          {/* Основной контент */}
          {showContent &&
            eventGroups.map((eventHeading, groupIndex) => {
              const groupEvents = futureEvents[eventHeading];
              const isLastGroup = groupIndex === eventGroups.length - 1;

              return (
                <div className="tbk-pt-4" key={eventHeading}>
                  <HeadlineWithSeparator
                    component="h2"
                    titleClass="tbk-bg-basic-white tbk-text-blue-grey tbk-text-h3-subtitle lg:tbk-text-title-bold"
                    className="tbk-mb-4 lg:tbk-mb-6"
                    separatorClass={"tbk--mt-2 lg:tbk--mt-2"}
                  >
                    {eventHeading}
                  </HeadlineWithSeparator>

                  <div className="tbk-mb-4 tbk-grid tbk-gap-2 md:tbk-grid-cols-2 lg:tbk-grid-cols-3">
                    {/* Реальные события */}
                    {groupEvents.map((evt) => (
                      <EventCard
                        key={evt.id}
                        href={`/events/${evt.id}`}
                        imgUrl={evt.coverPhoto}
                        title={evt.name}
                        startDate={evt.start}
                        endDate={evt.finish}
                        place={evt.location?.name}
                        joined={evt.joined}
                        waitlisted={evt.waitlisted}
                        soldOut={evt.capacity === evt.currentCapacity}
                        status={evt.status}
                        region={region}
                        timezone={evt.timezone}
                        onClick={handleEventCardClick}
                      />
                    ))}

                    {/* Плейсхолдеры только в последней группе */}
                    {isLastGroup &&
                      (isLoading || isFetchingNextPage) &&
                      [...Array(placeholderCount)].map((_, index) => (
                        <EventCardPlaceholder
                          key={`placeholder-${eventHeading}-${index}`}
                        />
                      ))}
                  </div>
                </div>
              );
            })}

          {/* Загрузка следующей страницы */}
          {showNextPageLoader && (
            <div className="tbk-pt-4">
              <HeadlineWithSeparator
                component="h2"
                titleClass="tbk-bg-basic-white tbk-text-blue-grey-light tbk-text-h3-subtitle lg:tbk-text-title-bold"
                className="tbk-mb-4 lg:tbk-mb-6"
                separatorClass={"tbk--mt-2 lg:tbk--mt-2"}
              >
                Loading More Events
              </HeadlineWithSeparator>
              <div className="tbk-mb-4 tbk-grid tbk-gap-2 md:tbk-grid-cols-2 lg:tbk-grid-cols-3">
                {[...Array(6)].map((_, index) => (
                  <EventCardPlaceholder key={`next-page-${index}`} />
                ))}
              </div>
            </div>
          )}

          {/* Сообщение, когда нет событий */}
          {!showContent && !isLoading && (
            <div className="tbk-mt-8 tbk-flex tbk-min-h-[370px] tbk-items-center tbk-justify-center">
              <div className="tbk-text-center tbk-text-blue-grey">
                No upcoming events found
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Кнопка "Show more" */}
      {hasNextPage && !isFetchingNextPage && (
        <div className="tbk-mb-7 tbk-flex tbk-justify-center">
          <Button
            variant="secondary"
            onClick={handleLoadMore}
            className="tbk-w-68"
          >
            Show more
          </Button>
        </div>
      )}

      {/* Элемент для автоматической подгрузки */}
      {/* {hasNextPage && <div ref={cursorRef} className="tbk-h-1" />} */}

      <div className="tbk-mb-7 tbk-flex tbk-justify-center">
        <Link to={"/lifestyle"}>
          <Button
            className="tbk-w-40 tbk-text-primary"
            variant="secondary"
            size={"md"}
            startIcon={<ArrowLeftIcon className="tbk-tex-primary" />}
          >
            Back
          </Button>
        </Link>
      </div>

      <Footer regionSelect={false} />
    </>
  );
}

const adaptCityName = (cityName) => {
  return cityName
    ? cityName === "LOS_ANGELES"
      ? "Los Angeles"
      : cityName === "SAN_DIEGO"
      ? "San Diego"
      : "All"
    : "All";
};
